/**
 * Retry utility for Supabase operations with exponential backoff
 * Reports persistent failures to Sentry
 */

import * as Sentry from '@sentry/react';

interface RetryOptions {
    /** Maximum number of retry attempts (default: 3) */
    maxRetries?: number;
    /** Base delay in ms before first retry (default: 1000) */
    baseDelay?: number;
    /** Maximum delay cap in ms (default: 10000) */
    maxDelay?: number;
    /** Context for Sentry error reporting */
    context?: {
        operation: string;
        userId?: string;
        [key: string]: unknown;
    };
    /** Whether to report to Sentry on final failure (default: true) */
    reportToSentry?: boolean;
}

/**
 * Check if an error is retryable (transient) vs permanent
 */
export function isRetryableError(error: unknown): boolean {
    if (!error) return false;

    // Check for network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
        return true;
    }

    // Check for Supabase/PostgrestError structure
    const err = error as { code?: string; status?: number; message?: string };

    // 5xx server errors are retryable
    if (err.status && err.status >= 500 && err.status < 600) {
        return true;
    }

    // Specific retryable error codes
    const retryableCodes = [
        'PGRST503', // Service unavailable
        'PGRST504', // Gateway timeout
        '57014',    // Query canceled
        '40001',    // Serialization failure
        '40P01',    // Deadlock detected
    ];

    if (err.code && retryableCodes.includes(err.code)) {
        return true;
    }

    // Network-related error messages
    const retryableMessages = [
        'network',
        'timeout',
        'connection',
        'ECONNRESET',
        'ETIMEDOUT',
        'fetch failed',
        'Failed to fetch',
        'Load failed',
        'NetworkError',
    ];

    if (err.message) {
        const lowerMessage = err.message.toLowerCase();
        if (retryableMessages.some(msg => lowerMessage.includes(msg.toLowerCase()))) {
            return true;
        }
    }

    return false;
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
    // Exponential backoff: baseDelay * 2^attempt
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    // Add jitter (±25%) to prevent thundering herd
    const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
    const delay = exponentialDelay + jitter;
    // Cap at maxDelay
    return Math.min(delay, maxDelay);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wrap an async function with retry logic
 * 
 * @example
 * const result = await withRetry(
 *   () => saveCompletedPomodoro(userId, data),
 *   { context: { operation: 'saveCompletedPomodoro', userId } }
 * );
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const {
        maxRetries = 3,
        baseDelay = 1000,
        maxDelay = 10000,
        context = { operation: 'unknown' },
        reportToSentry = true,
    } = options;

    let lastError: unknown;
    let attempt = 0;

    while (attempt <= maxRetries) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            attempt++;

            // Log the attempt
            if (import.meta.env.DEV) {
                console.warn(`[Retry] ${context.operation} failed (attempt ${attempt}/${maxRetries + 1}):`, error);
            }

            // Don't retry if it's a permanent error or we've exhausted retries
            if (!isRetryableError(error) || attempt > maxRetries) {
                break;
            }

            // Wait before retrying
            const delay = calculateDelay(attempt - 1, baseDelay, maxDelay);
            if (import.meta.env.DEV) {
                console.log(`[Retry] Waiting ${Math.round(delay)}ms before retry...`);
            }
            await sleep(delay);
        }
    }

    // All retries exhausted - report to Sentry
    if (reportToSentry) {
        Sentry.captureException(lastError, {
            tags: {
                type: 'supabase-retry-exhausted',
                operation: context.operation,
                attempts: String(attempt),
            },
            extra: {
                ...context,
                lastError: lastError instanceof Error ? lastError.message : String(lastError),
            },
        });

        console.error(`[Retry] ${context.operation} failed after ${attempt} attempts. Reported to Sentry.`);
    }

    // Re-throw the last error
    throw lastError;
}

/**
 * Wrapper specifically for "fire and forget" operations like XP sync
 * Catches errors silently after retries, only reports to Sentry
 */
export async function withRetryAsync<T>(
    fn: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T | null> {
    try {
        return await withRetry(fn, options);
    } catch {
        // Swallow error after Sentry report - caller doesn't need to handle
        return null;
    }
}
